import { Check } from 'lucide-react';
import { Input } from '@/components/ui/field';
import { Switch } from '@/components/ui/toggles';
import {
  filedValueLabel,
  isMaskedValue,
  valueForInput,
  type FieldGroupView,
  type RecordField,
} from './record-fields';
import { cn } from '@/lib/cn';

export type FieldValue = string | boolean;

/**
 * What each group of fields is called on the paper form.
 *
 * "Filer" and "Amounts" are our words for them; a customer reading this sheet has the IRIS
 * copy of the same form in front of them, where the blocks are the payer, the recipient and
 * the numbered boxes.
 */
const BAND_TITLES: Record<string, string> = {
  Filer: "Payer's details",
  Recipient: "Recipient's details",
  Amounts: 'Amounts reported',
  State: 'State and local information',
  Other: 'Other entries on this record',
};

/** How many boxes sit side by side. Amounts are short, addresses are not. */
const BAND_COLUMNS: Record<string, string> = {
  Filer: 'sm:grid-cols-2',
  Recipient: 'sm:grid-cols-2',
  Amounts: 'sm:grid-cols-2 lg:grid-cols-3',
  State: 'sm:grid-cols-2 lg:grid-cols-3',
  Other: 'sm:grid-cols-2',
};

/**
 * One box of the form: a hairline rule around it, its caption in the top left corner, and
 * the value filling the rest of it.
 *
 * This is the whole reason the sheet reads as the form rather than as a settings page. A
 * label above a rounded input says "here is a field you may type in"; a caption in the corner
 * of a ruled box says "this is box 1a, and this is what was filed in it", which is what the
 * customer is checking.
 */
export function FormBox({
  caption,
  required,
  note,
  error,
  className,
  children,
}: {
  caption: string;
  required?: boolean;
  /** What was filed, when the value on screen no longer matches it. */
  note?: string | null;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        // The rule is painted by each box rather than shown through the gaps between them:
        // a grid whose background was the rule colour turned the empty half of an odd last
        // row into a grey slab, which is not a box and does not belong on the form.
        'flex min-h-[74px] flex-col gap-1 bg-surface px-3 py-2 shadow-[0_0_0_1px_var(--ts-form-rule)]',
        error && 'bg-[color:var(--ts-pill-danger-bg)]',
        className,
      )}
    >
      <p className="text-[11px] leading-4 font-medium text-[color:var(--ts-form-caption)]">
        {caption}
        {required ? <span className="text-[color:var(--ts-destructive)]"> *</span> : null}
      </p>
      <div className="flex-1">{children}</div>
      {error ? <p className="text-[11px] text-[color:var(--ts-pill-danger-text)]">{error}</p> : null}
      {!error && note ? <p className="text-[11px] text-muted-foreground">Filed: {note}</p> : null}
    </div>
  );
}

/** The heading that runs the width of the sheet, as the form's blocks are separated. */
function Band({ title }: { title: string }) {
  return (
    <p className="bg-[color:var(--ts-form-band)] px-3 py-1.5 text-[11px] font-semibold tracking-[0.08em] text-[color:var(--ts-form-caption)] uppercase">
      {title}
    </p>
  );
}

/** A ticked box on the form is a ticked box here, not the word "Yes". */
function IndicatorMark({ on }: { on: boolean }) {
  return (
    <span className="flex h-9 items-center gap-2">
      <span
        className={cn(
          'flex h-5 w-5 items-center justify-center border border-[color:var(--ts-form-rule)]',
          on && 'bg-primary-light',
        )}
        aria-hidden="true"
      >
        {on ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
      </span>
      <span className="text-base text-gray-3">{on ? 'Yes' : 'No'}</span>
    </span>
  );
}

interface BoxProps {
  field: RecordField;
  value: FieldValue;
  filed: unknown;
  editing: boolean;
  revealed: boolean;
  issue?: string;
  onChange: (value: FieldValue) => void;
  onReveal: () => void;
}

/** One field, in its box: read as printed, or typed into when the record is being fixed. */
export function RecordFormBox({ field, value, filed, editing, revealed, issue, onChange, onReveal }: BoxProps) {
  const shown = field.kind === 'indicator' ? (value === true ? 'Yes' : 'No') : String(value);
  const filedShown = filedValueLabel(field.kind, filed);
  const differs = filed !== undefined && filedShown !== (shown === '' ? 'empty' : shown);
  const id = `record-field-${field.name.replace(/[^A-Za-z0-9]/g, '-')}`;
  // A pinned field states what the e-file is, and is not a thing to type over.
  const readOnly = !editing || field.pinned;

  const printed = (
    <p
      id={id}
      aria-label={field.label}
      className={cn('flex min-h-9 items-center text-base break-words text-gray-3', field.kind === 'amount' && 'tabular-nums')}
    >
      {shown === '' ? (
        <span className="text-[color:var(--ts-placeholder)]">—</span>
      ) : field.kind === 'amount' ? (
        <>
          <span className="mr-1 text-muted-foreground">$</span>
          {shown}
        </>
      ) : (
        shown
      )}
    </p>
  );

  return (
    <FormBox
      caption={field.label}
      required={field.required}
      error={issue}
      note={differs ? filedShown : null}
    >
      {readOnly ? (
        field.kind === 'indicator' ? (
          <IndicatorMark on={value === true} />
        ) : (
          printed
        )
      ) : field.kind === 'indicator' ? (
        <span className="flex h-9 items-center gap-3">
          <Switch id={id} label={field.label} checked={value === true} onCheckedChange={(next) => onChange(next)} />
          <span className="text-base text-gray-3">{value === true ? 'Yes' : 'No'}</span>
        </span>
      ) : field.kind === 'tin' && isMaskedValue(value) && !revealed ? (
        <span className="flex h-9 items-center justify-between gap-2">
          <span className="text-base text-gray-3">{String(value)}</span>
          <button
            type="button"
            onClick={onReveal}
            className="text-sm font-medium text-link underline underline-offset-2"
          >
            Enter new value
          </button>
        </span>
      ) : (
        <Input
          id={id}
          aria-label={field.label}
          value={String(value)}
          inputMode={field.kind === 'amount' ? 'decimal' : undefined}
          autoComplete="off"
          spellCheck={false}
          // The same input as everywhere else in the app, inside the box. An earlier version
          // drew a bare underline here so the box would still read as printed; it read as
          // text that happened to be editable, and nobody could tell which boxes were.
          className={cn('mt-1', field.kind === 'amount' && 'tabular-nums')}
          onChange={(event) => onChange(event.target.value)}
          onBlur={(event) => {
            if (field.kind !== 'amount') return;
            onChange(valueForInput('amount', event.target.value));
          }}
        />
      )}
    </FormBox>
  );
}

/**
 * The record drawn as the copy of the form it was filed on.
 *
 * It used to be a column of labelled inputs, which said nothing about which box of the form
 * each value went in - a customer checking a record against the copy in front of them had to
 * translate every line. Here the blocks and the boxes are the form's, so the two can be read
 * side by side, and every field the form has is a box whether or not this record filled it:
 * an empty box is an answer.
 */
export function RecordFormSheet({
  groups,
  values,
  filedCanonical,
  editing,
  revealed,
  issueFor,
  onChange,
  onReveal,
  heading,
}: {
  groups: FieldGroupView[];
  values: Record<string, FieldValue>;
  filedCanonical: Record<string, unknown> | undefined;
  editing: boolean;
  revealed: string[];
  issueFor: (name: string) => string | undefined;
  onChange: (name: string, value: FieldValue) => void;
  onReveal: (name: string) => void;
  /** The form and year band across the top, which the e-file rather than the record knows. */
  heading?: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-[color:var(--ts-form-rule)]">
      {heading}
      {groups.map(({ group, fields }) => (
        <div key={group}>
          <Band title={BAND_TITLES[group] ?? group} />
          <div
            className={cn('grid gap-px bg-surface', BAND_COLUMNS[group] ?? 'sm:grid-cols-2')}
          >
            {fields.map((field) => (
              <RecordFormBox
                key={field.name}
                field={field}
                value={values[field.name] ?? ''}
                filed={filedCanonical?.[field.name]}
                editing={editing}
                revealed={revealed.includes(field.name)}
                issue={issueFor(field.name)}
                onChange={(next) => onChange(field.name, next)}
                onReveal={() => onReveal(field.name)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
